import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { isFioApiStale } from "../../schemas/paymentAccount";

/**
 * UC-007 REQ-EH-006 banner — zobrazí "FIO API nedostupné" pokud
 * `last_synced_at` chybí nebo je starší než 2h. Color-not-only signaling
 * (REQ-UI-018): Alert variant=warning vykresluje `!` icon + barvu + text.
 *
 * Princip #4 (sync transparency): uživatel nikdy nesmí vidět prezentaci dat
 * jako kompletní, pokud sync neproběhl < 2h.
 */

export interface FioApiUnavailableBannerProps {
  /** ISO 8601 timestamp posledního úspěšného sync nebo null pokud nikdy. */
  lastSyncedAt: string | null;
  /** Account label (např. "Petr S1") pro disambiguaci v textu banneru. */
  accountLabel: string;
  /** Test-only override; defaultně `new Date()`. */
  now?: Date;
}

export function FioApiUnavailableBanner({
  lastSyncedAt,
  accountLabel,
  now,
}: FioApiUnavailableBannerProps) {
  if (!isFioApiStale(lastSyncedAt, now)) return null;

  const lastSyncedText = formatLastSynced(lastSyncedAt);

  return (
    <InlineAlert
      variant="warning"
      title="FIO API nedostupné"
      className="fio-api-banner"
    >
      <span data-testid={`fio-api-banner-${accountLabel}`}>
        Účet <strong>{accountLabel}</strong> — poslední úspěšný sync:{" "}
        <code>{lastSyncedText}</code>. Data v UI nemusí odrážet aktuální stav
        plateb (práh 2 h).
      </span>
    </InlineAlert>
  );
}

function formatLastSynced(iso: string | null): string {
  if (!iso) return "nikdy";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}
